import clsx from 'clsx';
import React from 'react';
import { times } from 'lodash';
import { EmblaOptionsType } from 'embla-carousel';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';

import {
  PrevButton,
  NextButton,
  usePrevNextButtons,
} from './carousel-arrow-buttons';
import { DotButton, useDotButton } from './carousel-dot-buttons';

import style from './carousel.module.scss';

type PropType = {
  count: number
  options?: EmblaOptionsType,
  autoplayDelay?: number,
  hasDotMenu?: boolean,
  hasArrowMenu?: boolean,
  hasDrag?: boolean,
  renderSlide: (index: number) => React.ReactNode,
}

export const Carousel: React.FC<PropType> = (props) => {
  const {
    count,
    options,
    autoplayDelay,
    hasArrowMenu = true,
    hasDotMenu = true,
    hasDrag = true,
    renderSlide,
  } = props;

  // hasDrag turns off the mouse/finger drag (watchDrag in EmblaOptionsType)
  // we also disable it for single slide carouself regardless of options
  const carouselOptions = {
    ...options,
    ...(count === 1 || !hasDrag ? { watchDrag: false } : {}),
  };

  const [emblaRef, emblaApi] = useEmblaCarousel(carouselOptions, [
    Autoplay({
      playOnInit: count > 1 && Boolean(autoplayDelay),
      delay: autoplayDelay || 3000,
    }),
  ]);

  const { selectedIndex, scrollSnaps, onDotButtonClick } = useDotButton(emblaApi);
  const {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick,
  } = usePrevNextButtons(emblaApi);

  const renderArrowMenu = () => {
    if (!hasArrowMenu) {
      return null;
    }

    return (
      <div className={style.EmblaButtons}>
        <PrevButton onClick={onPrevButtonClick} disabled={prevBtnDisabled} />
        <NextButton onClick={onNextButtonClick} disabled={nextBtnDisabled} />
      </div>
    );
  };

  const renderDotMenu = () => {
    if (!hasDotMenu) {
      return null;
    }

    return (
      <div className={style.EmblaDots}>
        {scrollSnaps.map((snap, index) => (
          <DotButton
            key={`carousel-dot-${snap}`}
            onClick={() => onDotButtonClick(index)}
            className={clsx(style.EmblaDot, index === selectedIndex && style.EmblaDotSelected)}
            tabIndex={0}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>
    );
  };

  const renderNavigation = () => {
    if (count <= 1) {
      return null;
    }

    return (
      <div className={style.EmblaControls}>
        {renderArrowMenu()}
        {renderDotMenu()}
      </div>
    );
  };

  return (
    <section className={style.Embla}>
      <div className={style.EmblaViewport} ref={emblaRef}>
        <div className={style.EmblaContainer}>
          {times(count, (index) => (
            <div className={style.EmblaSlide} key={`slide-${index}`}>
              {renderSlide(index)}
            </div>
          ))}
        </div>
      </div>
      {renderNavigation()}
    </section>
  );
};
